/**
 * RetentionControl, sheet 43 callouts 2 to 4 (mission 10 WP16 T090 to T092; FR-017, FR-005's screen half,
 * FR-022, FR-024; D.2, A.3, A.5, NF.9, UC-04; spec amendments 19, 26; OR-15, U17, U19, U31; style guide §7.1,
 * §7.2, §7.5, §8 "Settings"; contracts/retention-api.md "PATCH").
 *
 * - The one control: a whole number of days from `terms.min_days` to `terms.max_days`, labelled
 *   `retentionLabel` (the card's h3), `retentionHelp` with `terms.default_days`, and `retentionBounds` always
 *   beneath it; a value outside the bounds marks the input invalid and sends nothing.
 * - `retentionLastChanged` from `last_changed_at` links to the audit log's `retention_changed` filter (A.5),
 *   absent while the window never changed.
 * - `retentionNoDigestNote` while the window (typed, else saved) is below `terms.digest_min_window_days`,
 *   the digest's own rule (NF.9; U19), never a literal; it names that minimum less one (counsel C2).
 * - The sticky save bar (`retentionUnsaved`, Discard, Save) only while the value differs from the saved one,
 *   directly after the card so Tab reaches Save before the cards below (FR-024).
 * - At 0, `RetentionZeroWarning`; while the value is a change to 0 it carries its acknowledgment (a window already
 *   saved at 0 shows the warning alone; mission 10 review UI3), and until it is ticked, Save is the demoted, relabelled
 *   `RetentionZeroGatedAction` (`retentionZeroSaveGated`), never a disabled primary; ticked, Save sends
 *   `acknowledge_zero: true` (OR-15, U17). While the shell already shows a band (dunning or read-only) the
 *   warning renders inline in this card, never as a third band (§7.5; U31).
 * - The 409 `retention-confirmation-required` opens ShortenDialog with its count and runs; its Confirm re-sends
 *   with `confirm_due_count`, and a second 409 replaces the list. Any other refusal renders its band.
 */
import { useId, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { RetentionZeroGatedAction, RetentionZeroWarning } from '@/components/trellis/RetentionZeroWarning'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/components/ui/cn'
import { Input } from '@/components/ui/input'
import { ApiError } from '@/lib/api'
import type { RetentionPatchBody, RetentionTerms, RetentionWindow } from '@/lib/api/retention'
import { date } from '@/lib/format'
import { PROVISIONAL_COPY, copyText } from '@/lib/provisional-copy'
import { retentionConfirmation, useRetentionMutation, type RetentionConfirmation } from '@/lib/queries'

import { WithDate } from './dates'
import { ShortenDialog } from './ShortenDialog'

/** Where the provenance links: the audit log filtered to window changes (A.5). */
export const RETENTION_AUDIT_LINK = '/app/settings/audit?type=retention_changed'

const LINK = 'text-[var(--color-link)] underline hover:text-[var(--color-link-hover)]'

/** A whole number of days within the terms' bounds, else null. */
function parseDays(value: string, terms: RetentionTerms): number | null {
  if (!/^\d+$/.test(value)) return null
  const days = Number(value)
  return days >= terms.min_days && days <= terms.max_days ? days : null
}

export interface RetentionControlProps {
  /** The window read's first page. */
  window: RetentionWindow
  /** The shell already shows a band (dunning or read-only), so the retention-0 warning renders inline (U31). */
  shellBand: boolean
}

export function RetentionControl({ window: stored, shellBand }: RetentionControlProps) {
  const { terms } = stored
  const mutation = useRetentionMutation()
  const [baseline, setBaseline] = useState(stored.retention_days)
  const [value, setValue] = useState(String(stored.retention_days))
  const [invalid, setInvalid] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const [confirmation, setConfirmation] = useState<RetentionConfirmation | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const saveBar = useRef<HTMLDivElement>(null)
  const inputId = useId()
  const helpId = useId()
  const boundsId = useId()
  const ackId = useId()

  const parsed = parseDays(value, terms)
  const dirty = value !== String(baseline)
  const zero = parsed === 0
  const shown = parsed ?? baseline
  const failure = mutation.error !== null && retentionConfirmation(mutation.error) === null ? mutation.error : null

  function change(next: string) {
    setValue(next)
    setInvalid(false)
    setAcknowledged(false)
  }

  function send(extra: Pick<RetentionPatchBody, 'confirm_due_count'> = {}) {
    if (parsed === null) {
      setInvalid(true)
      inputRef.current?.focus()
      return
    }
    const body: RetentionPatchBody = { retention_days: parsed, ...extra }
    if (parsed === 0) body.acknowledge_zero = true
    mutation.mutate(body, {
      onSuccess: (result) => {
        setConfirmation(null)
        setBaseline(result.retention_days)
        setValue(String(result.retention_days))
        setAcknowledged(false)
      },
      onError: (err) => setConfirmation(retentionConfirmation(err)),
    })
  }

  function submit(event: FormEvent) {
    event.preventDefault()
    if (mutation.isPending) return
    if (zero && !acknowledged) {
      document.getElementById(ackId)?.focus()
      return
    }
    send()
  }

  function discard() {
    change(String(baseline))
    mutation.reset()
    inputRef.current?.focus()
  }

  /** Back to Save after the dialog (its last button), or to the control once the bar is gone. */
  function returnFocus() {
    const buttons = saveBar.current?.querySelectorAll('button')
    const save = buttons?.[buttons.length - 1]
    if (save) save.focus()
    else inputRef.current?.focus()
  }

  return (
    <>
      <form onSubmit={submit} noValidate className="flex flex-col gap-4">
        <Card>
          <CardHeader>
            <CardTitle>
              <label htmlFor={inputId}>{copyText('retentionLabel')}</label>
            </CardTitle>
            <CardDescription id={helpId}>
              {PROVISIONAL_COPY.retentionHelp.render({
                days: terms.default_days,
              })}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Input
                ref={inputRef}
                id={inputId}
                type="number"
                inputMode="numeric"
                min={terms.min_days}
                max={terms.max_days}
                step={1}
                className="max-w-32 font-mono"
                value={value}
                onChange={(e) => change(e.target.value)}
                autoComplete="off"
                aria-invalid={invalid ? true : undefined}
                aria-describedby={`${helpId} ${boundsId}`}
              />
              <p id={boundsId} className={cn('text-caption', invalid ? 'text-[var(--color-error-700)]' : 'text-navy-600')}>
                {PROVISIONAL_COPY.retentionBounds.render({
                  min: terms.min_days,
                  max: terms.max_days,
                })}
              </p>
            </div>
            {shown < terms.digest_min_window_days ? (
              <p className="text-body-sm text-navy-700">
                {/* Counsel C2 names the longest window without an email: one day under the digest minimum. */}
                {PROVISIONAL_COPY.retentionNoDigestNote.render({
                  days: terms.digest_min_window_days - 1,
                })}
              </p>
            ) : null}
            {zero ? (
              <RetentionZeroWarning
                hours={terms.zero_fallback_hours}
                minutes={terms.sweep_interval_minutes}
                inline={shellBand}
                // Mission 10 review UI3: the acknowledgment gates Save, so only a change to 0 offers it; a window
                // already saved at 0 keeps the warning alone.
                {...(dirty
                  ? {
                      acknowledgment: {
                        id: ackId,
                        checked: acknowledged,
                        onCheckedChange: setAcknowledged,
                      },
                    }
                  : {})}
              />
            ) : null}
            {stored.last_changed_at === null ? null : (
              <p className="text-caption text-navy-600">
                <Link to={RETENTION_AUDIT_LINK} className={LINK}>
                  <WithDate
                    text={PROVISIONAL_COPY.retentionLastChanged.render({
                      date: date(stored.last_changed_at),
                    })}
                    iso={stored.last_changed_at}
                  />
                </Link>
              </p>
            )}
          </CardContent>
        </Card>

        {failure === null ? null : failure instanceof ApiError ? (
          <ProblemBand problem={failure.problem} />
        ) : (
          <Band kind="blocked">
            <p>{copyText('genericError')}</p>
          </Band>
        )}

        {dirty ? (
          <div
            ref={saveBar}
            data-save-bar=""
            className="sticky bottom-0 -mx-[var(--content-padding)] flex h-14 items-center justify-end gap-3 border-t border-navy-200 bg-white px-[var(--content-padding)]"
          >
            <p className="mr-auto text-body-sm text-navy-700">{copyText('retentionUnsaved')}</p>
            <Button variant="ghost" onClick={discard}>
              {copyText('retentionDiscard')}
            </Button>
            {zero ? (
              <RetentionZeroGatedAction
                gate="save"
                acknowledged={acknowledged}
                acknowledgmentId={ackId}
                loading={mutation.isPending}
                onClick={() => send()}
              >
                {copyText('retentionSave')}
              </RetentionZeroGatedAction>
            ) : (
              <Button type="submit" loading={mutation.isPending}>
                {copyText('retentionSave')}
              </Button>
            )}
          </div>
        ) : null}
      </form>

      <ShortenDialog
        open={confirmation !== null}
        confirmation={confirmation}
        days={shown}
        minutes={terms.sweep_interval_minutes}
        pending={mutation.isPending}
        onConfirm={(extra) => send(extra)}
        onCancel={() => {
          setConfirmation(null)
          mutation.reset()
        }}
        onReturnFocus={returnFocus}
      />
    </>
  )
}
